// The registry of values that differ between regions or between deployments
// (Admin Settings → Region & deployment). Each deployment serves one region;
// its effective settings are that region's presets overlaid with whatever the
// admin overrode, stored as JSON on the region_settings row. Adding a setting
// is one entry here — its type, presets and validation come with it, and no
// migration is needed. The US presets are exactly what the CRM did before the
// setting existed.

import type { Region, RegionConfig, RegionSettingInput, RegionSettingKey, RegionSettingScope } from "./types/region.js";

export interface RegionSettingOption {
  value: string;
  label: string;
}

type Normalized<T> = { ok: true; value: T } | { ok: false; error: string };

export interface RegionSettingDefinition<T> {
  label: string;
  /** One sentence for the admin: what the setting decides. */
  description: string;
  input: RegionSettingInput;
  /** The allowed values of a "choice" setting, with what the admin sees. */
  options?: readonly RegionSettingOption[];
  /** False until some part of the CRM reads the setting. */
  inUse: boolean;
  /** "deployment" settings belong to one install, not to its region: every
   * region presets the same value, so switching region never moves them —
   * except a customer-facing address, which only the US presets (the hosts
   * are the US install's), so a non-US install starts with none of its own. */
  scope: RegionSettingScope;
  presets: Readonly<Record<Region, T>>;
  /** The environment variable that configured the setting before it was in
   * Admin. A valid value there stands in for the preset, so a deployment that
   * set it keeps its value until an admin overrides it. */
  env?: string;
  /** The canonical form of an admin-entered value, or why it is refused
   * (worded to follow the label). */
  normalize(value: unknown): Normalized<T>;
}

// The codes vLLM's transcription endpoint accepts (Whisper's ISO 639-1 set):
// Norwegian is "no" there, not "nb". Each speech-to-text provider gets its own
// spelling of the code from backend/src/lib/stt-language.ts.
export const STT_LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "no", label: "Norwegian" },
  { value: "auto", label: "Detect per clip" },
] as const;
export type SttLanguage = (typeof STT_LANGUAGE_OPTIONS)[number]["value"];

const ok = <T>(value: T): Normalized<T> => ({ ok: true, value });
const refuse = (error: string): Normalized<never> => ({ ok: false, error });

function trimmed(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/** The zone in Intl's casing (america/chicago -> America/Chicago), so the
 * same zone always stores and compares equal. Only the casing is taken: Intl
 * also resolves some current names to their legacy aliases (Asia/Kolkata ->
 * Asia/Calcutta, Europe/Kyiv -> Europe/Kiev), and those keep the name typed.
 * Throws RangeError for a zone Intl does not know. */
export function canonicalTimeZone(zone: string): string {
  const resolved = new Intl.DateTimeFormat("en-US", { timeZone: zone }).resolvedOptions().timeZone;
  return resolved.toLowerCase() === zone.toLowerCase() ? resolved : zone;
}

function timeZoneValue(value: unknown): Normalized<string> {
  const zone = trimmed(value);
  if (zone) {
    try {
      return ok(canonicalTimeZone(zone));
    } catch {
      // Unknown zone: refused below.
    }
  }
  return refuse("must be an IANA timezone, e.g. America/Chicago");
}

function timeZoneListValue(value: unknown): Normalized<readonly string[]> {
  if (!Array.isArray(value) || value.length === 0) {
    return refuse("must list at least one timezone");
  }
  const zones: string[] = [];
  for (const entry of value) {
    const zone = timeZoneValue(entry);
    if (!zone.ok) {
      return refuse(`has "${String(entry)}", which is not an IANA timezone`);
    }
    if (!zones.includes(zone.value)) zones.push(zone.value);
  }
  return ok(zones);
}

function localeValue(value: unknown): Normalized<string> {
  try {
    const [canonical] = Intl.getCanonicalLocales(trimmed(value));
    if (canonical) return ok(canonical);
  } catch {
    // Malformed tag: refused below.
  }
  return refuse("must be a BCP 47 locale, e.g. en-US");
}

function codeValue(pattern: RegExp, casing: "upper" | "lower", example: string): (value: unknown) => Normalized<string> {
  return (value) => {
    const code = casing === "upper" ? trimmed(value).toUpperCase() : trimmed(value).toLowerCase();
    return pattern.test(code) ? ok(code) : refuse(`must be ${example}`);
  };
}

/** An absolute http(s) address with no query or fragment, stored without a
 * trailing slash so callers can append paths. */
function publicUrlValue(value: unknown): Normalized<string> {
  let url: URL | null = null;
  try {
    url = new URL(trimmed(value));
  } catch {
    // Not a URL: refused below.
  }
  if (!url || (url.protocol !== "https:" && url.protocol !== "http:") || url.username || url.password || url.search || url.hash) {
    return refuse("must be a full web address with no query, e.g. https://customer.example.com");
  }
  return ok(`${url.origin}${url.pathname}`.replace(/\/+$/, ""));
}

/** A DNS zone such as example.com, lowercased, or blank for none. */
function domainZoneValue(value: unknown): Normalized<string> {
  const zone = trimmed(value).toLowerCase();
  if (!zone) return ok("");
  return /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(zone)
    ? ok(zone)
    : refuse("must be a domain like example.com, or blank");
}

const EMAIL_ADDRESS = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>.]+$/;

/** A From header: "Name <address>" or a bare address. Blank is allowed and
 * means "no fixed sender". */
function emailSenderValue(value: unknown): Normalized<string> {
  const sender = trimmed(value);
  if (!sender) return ok("");
  const named = /^([^<>\r\n]*?)\s*<([^<>]+)>$/.exec(sender);
  const name = named ? named[1].trim() : "";
  const address = (named ? named[2] : sender).trim();
  if (!EMAIL_ADDRESS.test(address)) {
    return refuse('must be an address like "Name <noreply@example.com>" or a bare address, or blank');
  }
  return ok(name ? `${name} <${address}>` : address);
}

function sttLanguageValue(value: unknown): Normalized<SttLanguage> {
  const code = trimmed(value).toLowerCase();
  const option = STT_LANGUAGE_OPTIONS.find((candidate) => candidate.value === code);
  return option
    ? ok(option.value)
    : refuse(`must be one of ${STT_LANGUAGE_OPTIONS.map((candidate) => candidate.label).join(", ")}`);
}

const countryCodeValue = codeValue(/^[A-Z]{2}$/, "upper", "a two-letter ISO 3166 country code, e.g. US");

function countryCodeListValue(value: unknown): Normalized<readonly string[]> {
  if (!Array.isArray(value) || value.length === 0) {
    return refuse("must list at least one country");
  }
  const codes: string[] = [];
  for (const entry of value) {
    const code = countryCodeValue(entry);
    if (!code.ok) {
      return refuse(`has "${String(entry)}", which is not a two-letter ISO 3166 country code`);
    }
    if (!codes.includes(code.value)) codes.push(code.value);
  }
  return ok(codes);
}

// Pins each entry's value type so RegionSettings can be read off the registry.
function setting<T>(definition: RegionSettingDefinition<T>): RegionSettingDefinition<T> {
  return definition;
}

// A deployment setting: one default for every region.
function deploymentSetting<T>(
  definition: Omit<RegionSettingDefinition<T>, "scope" | "presets"> & { default: T }
): RegionSettingDefinition<T> {
  const { default: value, ...rest } = definition;
  return { ...rest, scope: "deployment", presets: { US: value, NO: value } };
}

export const REGION_SETTINGS = {
  defaultTimezone: setting<string>({
    label: "Default timezone",
    description:
      "Times show in this zone wherever no campaign or person has a zone of their own. The Dialing page's default timezone is this same setting. Contacts with no zone of their own follow the business-day timezone instead.",
    input: "text",
    inUse: true,
    scope: "region",
    presets: { US: "America/Chicago", NO: "Europe/Oslo" },
    normalize: timeZoneValue,
  }),
  businessDayTimezone: setting<string>({
    label: "Business-day timezone",
    description:
      "Whose midnight starts a new business day for daily counts, ops dates and lifecycle dates. Contacts and AI agents with no zone of their own schedule in it too.",
    input: "text",
    inUse: true,
    scope: "region",
    presets: { US: "America/New_York", NO: "Europe/Oslo" },
    normalize: timeZoneValue,
  }),
  timezoneChoices: setting<readonly string[]>({
    label: "Timezone choices",
    description: "The zones offered in timezone pick-lists, in order.",
    input: "list",
    inUse: true,
    scope: "region",
    presets: {
      US: ["America/Chicago", "America/New_York", "America/Denver", "America/Los_Angeles"],
      NO: ["Europe/Oslo"],
    },
    normalize: timeZoneListValue,
  }),
  locale: setting<string>({
    label: "Locale",
    description:
      "How numbers, amounts and dates are written on CRM screens, in customer emails, texts and contracts, and in Discord posts, e.g. en-US or nb-NO. Screens that follow each viewer's own browser language keep doing so.",
    input: "text",
    inUse: true,
    scope: "region",
    presets: { US: "en-US", NO: "nb-NO" },
    normalize: localeValue,
  }),
  currency: setting<string>({
    label: "Currency",
    description:
      "The currency new deals start in, which their payment plans, contracts and charges follow, and the one an amount without a currency of its own is shown in. Existing deals keep the currency they have.",
    input: "text",
    inUse: true,
    scope: "region",
    presets: { US: "USD", NO: "NOK" },
    normalize: codeValue(/^[A-Z]{3}$/, "upper", "a three-letter ISO 4217 code, e.g. USD"),
  }),
  phoneCountry: setting<string>({
    label: "Phone country",
    description:
      "The country whose numbers calls go out on, and the one a number written without a country code belongs to. A call never goes out on a number from outside this country and the dialed number's own. The US dialing rules (the US48 zone, the area-code restriction, and buying numbers from Telnyx) apply only when this is US.",
    input: "text",
    inUse: true,
    scope: "region",
    presets: { US: "US", NO: "NO" },
    normalize: countryCodeValue,
  }),
  dialCountries: setting<readonly string[]>({
    label: "Dialable countries",
    description:
      "The countries a contact's phone number may be from. A number from anywhere else is refused when a contact is imported, created or edited.",
    input: "list",
    inUse: true,
    scope: "region",
    presets: { US: ["US", "NO", "PH", "AU"], NO: ["NO"] },
    normalize: countryCodeListValue,
  }),
  sttLanguage: setting<SttLanguage>({
    label: "Transcription language",
    description:
      "The spoken language call and meeting recordings and live call audio are transcribed in, on every speech-to-text provider. A recording whose provider cannot transcribe the language waits, with the reason on this page, until the provider or the language changes.",
    input: "choice",
    options: STT_LANGUAGE_OPTIONS,
    inUse: true,
    scope: "region",
    presets: { US: "en", NO: "no" },
    env: "STT_VLLM_LANGUAGE",
    normalize: sttLanguageValue,
  }),
  previewAliasZone: setting<string>({
    label: "Demo website link domain",
    description:
      "Demo website links show on this domain (acme.example.com instead of acme.demo-previews.com) in the CRM, the customer portal, and outbound emails and texts. The domain must serve the demo sites. Blank keeps the demo-previews.com links.",
    input: "text",
    inUse: true,
    scope: "region",
    presets: { US: "regen-digital.com", NO: "" },
    normalize: domainZoneValue,
  }),
  portalBaseUrl: setting<string>({
    label: "Customer portal address",
    description:
      "Where customers sign in to their portal. Portal emails link here.",
    input: "text",
    inUse: true,
    scope: "deployment",
    presets: { US: "https://customer.regen-digital.com", NO: "" },
    env: "PORTAL_BASE_URL",
    normalize: publicUrlValue,
  }),
  bookingPortalBaseUrl: setting<string>({
    label: "Booking page address",
    description:
      "Where customers book a meeting themselves. Every booking link handed out, in texts and emails, starts with it, and images in customer emails load from here.",
    input: "text",
    inUse: true,
    scope: "deployment",
    presets: { US: "https://cal.regen-digital.com", NO: "" },
    env: "CAL_PUBLIC_BASE_URL",
    normalize: publicUrlValue,
  }),
  portalEmailFrom: deploymentSetting<string>({
    label: "Customer portal email sender",
    description:
      "The From address of portal sign-in links and change-request replies. Blank sends from noreply@ at the customer's operating company's domain.",
    input: "text",
    inUse: true,
    default: "",
    env: "PORTAL_EMAIL_FROM",
    normalize: emailSenderValue,
  }),
};

/** The US48 zone, the area-code restriction and buying numbers from Telnyx
 * are US rules: they apply only where the region's phone country is the US. */
export function followsUsDialingRules(config: Pick<RegionConfig, "phoneCountry">): boolean {
  return config.phoneCountry === "US";
}

const NEW_STAFF_WORKING_HOURS_BUCKET: Readonly<Record<Region, "US" | "EU">> = { US: "US", NO: "EU" };

/** The working-hours bucket a new staff member starts in when none is picked:
 * the one whose hours the region's staff work. Existing staff keep theirs. */
export function newStaffWorkingHoursBucket(config: Pick<RegionConfig, "region">): "US" | "EU" {
  return NEW_STAFF_WORKING_HOURS_BUCKET[config.region];
}

/** The zone a contact is scheduled, called and spoken to in: its own, else
 * the business-day timezone. Scheduling, deal follow-ups and the AI agents
 * resolve a zoneless contact's zone here, so a setter's booking, a callback
 * and an AI agent never place the same contact in different zones. */
export function resolveContactTimezone(
  own: string | null | undefined,
  config: Pick<RegionConfig, "businessDayTimezone">
): string {
  return own?.trim() || config.businessDayTimezone;
}

export const REGION_SETTING_KEYS = Object.keys(REGION_SETTINGS) as RegionSettingKey[];

export function isRegionSettingKey(value: string): value is RegionSettingKey {
  return Object.hasOwn(REGION_SETTINGS, value);
}

// Generic in K so the preset and the override resolve to the same value type.
function definitionOf<K extends RegionSettingKey>(key: K): RegionSettingDefinition<RegionConfig[K]> {
  return REGION_SETTINGS[key] as RegionSettingDefinition<RegionConfig[K]>;
}

export function regionSettingPreset<K extends RegionSettingKey>(region: Region, key: K): RegionConfig[K] {
  return definitionOf(key).presets[region];
}

type Env = Readonly<Record<string, string | undefined>>;

/** The setting's value from its environment variable, or null when the
 * variable is unset, blank or invalid. */
export function regionSettingFromEnv<K extends RegionSettingKey>(key: K, env: Env): RegionConfig[K] | null {
  const definition = definitionOf(key);
  const raw = definition.env ? env[definition.env]?.trim() : undefined;
  if (!raw) return null;
  const stored = definition.normalize(raw);
  return stored.ok ? stored.value : null;
}

/** What a setting is without an override: its environment value, else the region's preset. */
export function regionSettingFallback<K extends RegionSettingKey>(region: Region, key: K, env: Env): RegionConfig[K] {
  return regionSettingFromEnv(key, env) ?? regionSettingPreset(region, key);
}

/** The stored override of one setting, or null when there is none — or when
 * the stored value no longer passes the setting's validation, so a tightened
 * rule falls back to the preset instead of reaching a caller. */
export function regionSettingOverride<K extends RegionSettingKey>(
  key: K,
  overrides: Readonly<Record<string, unknown>>
): RegionConfig[K] | null {
  if (!Object.hasOwn(overrides, key)) return null;
  const stored = definitionOf(key).normalize(overrides[key]);
  return stored.ok ? stored.value : null;
}

/** Whether a deployment setting of a non-US deployment has no value because
 * nothing set it: no override, no environment value, and only the US presets
 * one. A setting blank everywhere means "nothing fixed" and never counts; nor
 * does a setting no part of the CRM reads yet. Region settings have presets
 * of their own and never count. */
export function regionSettingUnset<K extends RegionSettingKey>(
  region: Region,
  key: K,
  overrides: Readonly<Record<string, unknown>>,
  env: Env
): boolean {
  const definition = definitionOf(key);
  if (region === "US" || definition.scope !== "deployment" || !definition.inUse) return false;
  if (regionSettingOverride(key, overrides) !== null || regionSettingFromEnv(key, env) !== null) return false;
  return regionSettingPreset(region, key) === "" && regionSettingPreset("US", key) !== "";
}

/** The region's presets overlaid with its valid overrides; unregistered keys
 * are ignored. `env` is the server's environment — the browser has none, and
 * reads the resolved config from the backend. */
export function resolveRegionConfig(region: Region, overrides: Readonly<Record<string, unknown>>, env: Env = {}): RegionConfig {
  const settings = Object.fromEntries(
    REGION_SETTING_KEYS.map((key) => [key, regionSettingOverride(key, overrides) ?? regionSettingFallback(region, key, env)])
  );
  return { ...settings, region } as RegionConfig;
}
